/******************************************************************************
 * @Author                : Jbristhuille<jbristhuille@gmail.com>              *
 * @CreatedDate           : 2024-11-04 16:11:34                               *
 * @LastEditors           : Jbristhuille<jbristhuille@gmail.com>              *
 * @LastEditDate          : 2025-01-23 13:56:48                               *
 *****************************************************************************/

/* SUMMARY
  * Imports
  * Services
  * Sections
  * Components
  * Styles
  * Workorders: Workorders list view
    * useEffect: get workorders list
    * useEffect: paginate workorders list
    * loadWorkordersWithFilters: charger les workorders avec filtres
    * handleFilterSubmit: gérer la soumission du formulaire de filtrage
    * clearFilter: supprimer un filtre
    * clearAllFilters: supprimer tous les filtres
*/

/* Imports */
import { useEffect, useState, useCallback, useMemo } from "react";
import { t } from "i18next";
/***/

/* Services */
import {
  createSingleQuery,
  SearchOptions,
  SearchQuery,
  toSearchOrder,
  searchWithFilters
} from "../../services/api/search";
/***/

/* Hooks */
import { useWorkordersFilters } from "../../hooks/filters";
/***/

/* Sections */
import { WorkordersTable } from "./sections/WorkordersTable";
/***/

/* Components */
import { Pagination } from "../../components/pagination/Pagination";
import { GenericListLayout } from "../../components/generic-list-view/GenericListLayout";
import FilterDrawer from "../../components/filter-drawer/FilterDrawer";
import WorkordersFilterForm, { getQueriesFromFilter } from "./filter/WorkordersFilterForm";
import FiltersDisplay from "../../components/filter/FiltersDisplay";
/***/

/* Styles */
import "./Workorders.scss";
import { workorderExportDescriptor } from "../../services/export/data/workorderExport";
import ExportButton from "../../components/export-button/ExportButton";
import { useDrawerLayoutDispatch } from "../../components/drawer-layout/DrawerLayout";
import WorkordersCustomerFilterForm from "./filter/WorkordersCustomerFilterForm";
import { IconButton } from "@material-tailwind/react";
import PartsExportForm from "./components/PartsExportForm";
import { ScissorsIcon } from "@heroicons/react/24/solid";
import { useTablePageSize } from "../../hooks/useTablePageSize";
/***/

/**
 * Composant principal pour la gestion des workorders
 * Gère l'affichage, le filtrage, la pagination et l'export des workorders
 */
export function Workorders({
  isAdminComponent,
  customer,
  hideHeader,
  hideFilters,
  noPadding
}: {
  isAdminComponent?: boolean,
  customer?: EasyTech.Customer,
  hideHeader?: boolean,
  hideFilters?: boolean,
  noPadding?: boolean
}) {
  const [workordersPageState, setWorkordersPageState] = useState<EasyTech.Workorder[]>([]);
  const [pageNumberState, setPageNumberState] = useState<number>(1);
  const [pageState, setPageState] = useState<number>(0);
  const [orderState, setOrderState] = useState<{ head: string, orderDesc: boolean }>({ head: "created", orderDesc: true });
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const pageSize = useTablePageSize();

  /* Un changement de taille invalide le curseur de page : l'élément 181 est en page 9 à 20 par page
   * et en page 2 à 100. Repartir de la première page est le seul comportement qui ne montre jamais
   * une page vide. */
  useEffect(() => { setPageState(0); }, [pageSize]);

  const onOrderChange = (head: string, orderDesc: boolean) => {
    setOrderState({ head, orderDesc });
  };

  const showBreadcrumb = () => {
    if ((isAdminComponent === undefined || isAdminComponent === false) && !customer)
      return true;
    return false;
  };

  // Fonction pour réinitialiser la page
  const resetPage = useCallback(() => {
    setPageState(0);
  }, []);

  // Utilisation du hook de filtres
  const {
    filters,
    displayFilters,
    handleFilterSubmit,
    clearFilter,
    clearAllFilters
  } = useWorkordersFilters(resetPage);

  /**
   * Le périmètre que le drawer de filtres ne connaît pas : ouverte depuis la fiche d'un client,
   * cette liste est celle de ses interventions. L'export doit recevoir la même restriction, sinon
   * il renvoie les interventions de tout le realm depuis une page qui n'en montre qu'une.
   */
  const customerScope: SearchQuery[] = useMemo(
    () => (customer ? [createSingleQuery("customer.id", customer.id)] : []),
    [customer]
  );

  /** Le tri affiché. Une seule définition, pour que l'export ne puisse pas diverger de la table. */
  const searchOrder = useMemo(
    () => toSearchOrder(orderState.head, orderState.orderDesc),
    [orderState]
  );

  /**
   * Charge les workorders filtrés depuis l'API
   * Gère les différents types de filtres (référence, technicien, client, statut, dates)
   */
  const loadWorkordersWithFilters = useCallback(async () => {
    if (!filters) return;
    setIsLoading(true);
    try {
      const searchOptions: SearchOptions = {
        paginate: {
          pageSize,
          pageNumber: pageState,
        },
        order: searchOrder,
        queries: [...getQueriesFromFilter(filters), ...customerScope]
      };

      const response = await searchWithFilters<EasyTech.Workorder>(
        "workorders",
        searchOptions
      );

      if (response && response.data) {
        setWorkordersPageState(response.data);
        setPageNumberState(response.totalPages);
      } else {
        setWorkordersPageState([]);
        setPageNumberState(1);
      }
    } catch (error) {
      console.error("Erreur lors du chargement des workorders avec filtres:", error);
      setWorkordersPageState([]);
      setPageNumberState(1);
    } finally {
      setIsLoading(false);
    }
  }, [searchOrder, filters, pageState, pageSize, customerScope]);

  /**
   * Charge initialement tous les workorders depuis l'API
   */
  useEffect(() => {
    loadWorkordersWithFilters();
  }, [loadWorkordersWithFilters]);

  const headerActions = (
    <>
      <ExportButton<EasyTech.Workorder> filters={filters} entityName="workorders"
        fileName="interventions.xlsx"
        descriptor={workorderExportDescriptor} getQueryFromFilter={getQueriesFromFilter}
        order={searchOrder} extraQueries={customerScope}
      />
      <PartsExportDrawerButton />
    </>
  );

  const filterDrawerComponent = (
    <FilterDrawer<EasyTech.WorkordersFilterType>
      FormComponent={customer ? WorkordersCustomerFilterForm : WorkordersFilterForm}
      onSubmit={(data) => {
        // TODO: Refacto - Cette logique particulière de validation de date de RangeDatePicker a été ajoutée ici pour pallier le fait que le form est soumis depuis le bouton du Parent (FilterDrawer) sans déclencher la validation du formulaire de manière native.
        if (data.dateRange?.start && !data.dateRange?.end) return Promise.reject();
        if (data.dateRange?.end && !data.dateRange?.start) return Promise.reject();
        return handleFilterSubmit(data);
      }}
      entityName={t("workorder:title")}
      data={filters}
    />
  );

  const filterBadgesComponent = (
    <FiltersDisplay
      filters={displayFilters}
      onClearFilter={clearFilter}
      onClearAll={clearAllFilters}
      size="sm"
      compact={true}
    />
  );

  const mainContent = workordersPageState && workordersPageState.length > 0 ? (
    <WorkordersTable workorders={workordersPageState} onHeadClick={onOrderChange} onChange={() => loadWorkordersWithFilters()}
      selectedHead={orderState.head} orderDesc={orderState.orderDesc} isAdminComponent={isAdminComponent} customer={customer} />
  ) : (
    <div className="text-center py-8 bg-blue-gray-50 rounded-lg">
      <p className="text-lg text-blue-gray-700">
        {t("workorder:noData")}
      </p>
      <p className="text-sm text-blue-gray-500 mt-2">
        {filters && Object.keys(filters).length > 0
          ? t("workorder:noData.filters")
          : t("workorder:noData.add")}
      </p>
    </div>
  );

  const paginationComponent = workordersPageState && workordersPageState.length > 0 ? (
    <Pagination
      actualPage={pageState}
      pageNumber={pageNumberState}
      changePage={setPageState}
    />
  ) : null;

  return (
    <GenericListLayout
      entityName={t("workorder:title")}
      path="/workorders"
      isLoading={isLoading}
      headerActions={hideFilters ? null : headerActions}
      filterDrawer={hideFilters ? null : filterDrawerComponent}
      filterBadges={hideFilters ? null : filterBadgesComponent}
      content={mainContent}
      pagination={paginationComponent}
      className="workorders"
      showBreadcrumb={showBreadcrumb()}
      hideHeader={hideHeader}
      noPadding={noPadding}
    />
  );
}
/***/

// TODO : Create a new component for the parts export drawer button
function PartsExportDrawerButton() {
  const dispatch = useDrawerLayoutDispatch();
  const openDrawer = () => {
    dispatch({
      type: "open",
      title: "Export parts used",
      content: (
        <div className="p-4 h-[90%]">
          <PartsExportForm />
        </div>
      )
    });
  };
  return (
    <IconButton
      onClick={openDrawer}
      variant="text"
      className="icon-btn bg-white text-blue-gray-900 shadow-md shadow-blue-gray-500/10 hover:shadow-lg hover:shadow-blue-gray-500/20 focus:opacity-[0.85] focus:shadow-none active:opacity-[0.85] active:shadow-none ml-2"
      size="sm"
    >
      <ScissorsIcon className="w-[24px] h-[24px] text-black" />
    </IconButton>
  );
}
